// Initiative Leads Mapping Configuration
// This file maps initiative leads to the sub-awardees they are responsible for reviewing

export interface InitiativeLead {
  id: string;
  name: string;
  email?: string;
  azureEmails?: string[];  // Multiple email addresses they might use
  azureRoles?: string[];   // Azure AD roles that identify this lead
  adminGroups: string[];
  subawardees: string[];
  componentProjects: string[];
}

export interface SubawardeeInfo {
  id: string;
  displayName: string;
  componentProject: string;
  initiativeLead: string;
}

// Initiative Leads and their responsibilities
export const INITIATIVE_LEADS: InitiativeLead[] = [
  {
    id: 'tori-burris',
    name: 'Tori Burris',
    email: 'tori.burris@tulsa-innovation.org',
    azureEmails: ['tori.burris@tulsa-innovation.org', 'tburris@tulsa-innovation.org'],
    azureRoles: ['SAFE-T.LEAD', 'ASWP.LEAD'],
    adminGroups: ['SAFE-T.ADMIN', 'ASWP.ADMIN'],
    subawardees: [
      'Skyway Range',
      'TU - SAFE-T', 
      'DronePort Network',
      'Asemio',
      'inTulsa - ASWP'
    ],
    componentProjects: ['SAFE-T', 'ASWP']
  },
  {
    id: 'kenny-osborn',
    name: 'Kenny Osborn',
    email: 'kenny.osborn@tulsa-innovation.org',
    azureEmails: ['kenny.osborn@tulsa-innovation.org', 'kosborn@tulsa-innovation.org'],
    azureRoles: ['ASWP.LEAD'],
    adminGroups: ['ASWP.ADMIN'],
    subawardees: [
      'TU - ASWP',
      'Atlas'
    ],
    componentProjects: ['ASWP']
  },
  {
    id: 'drew-kincius',
    name: 'Drew Kincius',
    email: 'drew.kincius@tulsa-innovation.org',
    azureEmails: ['drew.kincius@tulsa-innovation.org', 'dkincius@tulsa-innovation.org'],
    azureRoles: ['ASWP.LEAD'],
    adminGroups: ['ASWP.ADMIN'],
    subawardees: [
      'THEC',
      'TCC'
    ],
    componentProjects: ['ASWP']
  },
  {
    id: 'tiff-henley',
    name: 'Tiff Henley',
    email: 'tiff.henley@tulsa-innovation.org',
    azureEmails: ['tiff.henley@tulsa-innovation.org', 'thenley@tulsa-innovation.org'],
    azureRoles: ['CNTRL.LEAD'],
    adminGroups: ['CNTRL.ADMIN'],
    subawardees: [
      'inTulsa - CNTRL'
    ],
    componentProjects: ['CNTRL']
  },
  {
    id: 'john-doe',
    name: 'John McDonald',
    email: 'john.doe@tulsa-innovation.org',
    azureEmails: ['john.doe@tulsa-innovation.org', 'jdoe@tulsa-innovation.org'],
    azureRoles: ['G-ACE.LEAD'],
    adminGroups: ['G-ACE.ADMIN'],
    subawardees: [
      'Black Tech Street'
    ],
    componentProjects: ['G-ACE']
  }
];

// Reverse mapping: Subawardee to Initiative Lead
export const SUBAWARDEE_TO_LEAD_MAP: Record<string, string> = {};
INITIATIVE_LEADS.forEach(lead => {
  lead.subawardees.forEach(subawardee => {
    SUBAWARDEE_TO_LEAD_MAP[subawardee] = lead.id;
  });
});

// All subawardees with their details
// displayName follows the "Name - PROJECT" convention used by the group selector
export const SUBAWARDEE_INFO: SubawardeeInfo[] = [
  // SAFE-T Component Project
  { id: 'Skyway Range', displayName: 'Skyway Range - SAFET', componentProject: 'SAFE-T', initiativeLead: 'tori-burris' },
  { id: 'TU - SAFE-T', displayName: 'TU - SAFET', componentProject: 'SAFE-T', initiativeLead: 'tori-burris' },
  { id: 'DronePort Network', displayName: 'DronePort Network - SAFET', componentProject: 'SAFE-T', initiativeLead: 'tori-burris' },
  
  // ASWP Component Project
  { id: 'Asemio', displayName: 'Asemio - ASWP', componentProject: 'ASWP', initiativeLead: 'tori-burris' },
  { id: 'inTulsa - ASWP', displayName: 'inTulsa - ASWP', componentProject: 'ASWP', initiativeLead: 'tori-burris' },
  { id: 'TU - ASWP', displayName: 'TU - ASWP', componentProject: 'ASWP', initiativeLead: 'kenny-osborn' },
  { id: 'Atlas', displayName: 'Atlas - ASWP', componentProject: 'ASWP', initiativeLead: 'kenny-osborn' },
  { id: 'THEC', displayName: 'THEC - ASWP', componentProject: 'ASWP', initiativeLead: 'drew-kincius' },
  { id: 'TCC', displayName: 'TCC - ASWP', componentProject: 'ASWP', initiativeLead: 'drew-kincius' },
  
  // CNTRL Component Project
  { id: 'inTulsa - CNTRL', displayName: 'inTulsa - CT', componentProject: 'CNTRL', initiativeLead: 'tiff-henley' },
  
  // G-ACE Component Project
  { id: 'Black Tech Street', displayName: 'Black Tech Street - GACE', componentProject: 'G-ACE', initiativeLead: 'john-doe' },
  
  // Additional groups that may not have specific leads assigned yet
  { id: 'TU - ARCH', displayName: 'TU - ARCH', componentProject: 'ARCH', initiativeLead: '' },
  { id: 'OSU', displayName: 'OSU - ARCH', componentProject: 'ARCH', initiativeLead: '' }
];

/**
 * Get the initiative lead responsible for a specific subawardee
 */
export function getInitiativeLeadForSubawardee(subawardeeId: string): InitiativeLead | null {
  const leadId = SUBAWARDEE_TO_LEAD_MAP[subawardeeId];
  if (!leadId) return null;
  
  return INITIATIVE_LEADS.find(lead => lead.id === leadId) || null;
}

/**
 * Get all subawardees assigned to a specific initiative lead
 */
export function getSubawardeesForLead(leadId: string): SubawardeeInfo[] {
  return SUBAWARDEE_INFO.filter(subawardee => subawardee.initiativeLead === leadId);
}

/**
 * Get all subawardees for a specific component project
 */
export function getSubawardeesForComponent(componentProject: string): SubawardeeInfo[] {
  return SUBAWARDEE_INFO.filter(subawardee => subawardee.componentProject === componentProject);
}

/**
 * Check if a user has access to a specific subawardee based on their admin groups
 */
export function hasAccessToSubawardee(userAdminGroups: string[], subawardeeId: string): boolean {
  const lead = getInitiativeLeadForSubawardee(subawardeeId);
  if (!lead) return false;
  
  // Check if user's admin groups overlap with the lead's admin groups
  return lead.adminGroups.some(group => userAdminGroups.includes(group));
}

/**
 * Get accessible subawardees for a user based on their admin groups
 */
export function getAccessibleSubawardees(userAdminGroups: string[]): SubawardeeInfo[] {
  return SUBAWARDEE_INFO.filter(subawardee => 
    hasAccessToSubawardee(userAdminGroups, subawardee.id)
  );
}

/**
 * Get initiative leads that a user can manage based on their admin groups
 */
export function getManageableLeads(userAdminGroups: string[]): InitiativeLead[] {
  return INITIATIVE_LEADS.filter(lead => 
    lead.adminGroups.some(group => userAdminGroups.includes(group))
  );
}

/**
 * Determine which initiative lead the current user is based on their auth info
 */
export function getCurrentUserInitiativeLead(user: {
  email?: string;
  roles?: string[];
  allGroups?: string[];
}): InitiativeLead | null {
  if (!user) return null;

  // First try to match by email
  if (user.email) {
    const emailMatch = INITIATIVE_LEADS.find(lead => 
      lead.azureEmails?.includes(user.email!) || lead.email === user.email
    );
    if (emailMatch) return emailMatch;
  }

  // Then try to match by Azure roles
  if (user.roles && user.roles.length > 0) {
    const roleMatch = INITIATIVE_LEADS.find(lead => 
      lead.azureRoles?.some(role => user.roles!.includes(role))
    );
    if (roleMatch) return roleMatch;
  }

  // Finally try to match by admin groups
  if (user.allGroups && user.allGroups.length > 0) {
    const groupMatch = INITIATIVE_LEADS.find(lead => 
      lead.adminGroups.some(group => user.allGroups!.includes(group))
    );
    if (groupMatch) return groupMatch;
  }

  return null;
}

/**
 * Filter component projects based on user's initiative lead assignment
 */
export function getFilteredComponentProjects(user: any, allProjects: any[]): any[] {
  // If user has admin role, show all projects
  if (user?.roles?.includes('TIL.PMP.ADMIN')) {
    return allProjects;
  }

  const userLead = getCurrentUserInitiativeLead(user);
  if (!userLead) {
    return allProjects; // Default to all if we can't determine lead
  }

  // Filter projects to only those assigned to this lead
  return allProjects.filter(project => 
    userLead.componentProjects.includes(project.name)
  );
}

/**
 * Filter quarterly reports based on user's initiative lead assignment
 */
export function getFilteredQuarterlyReports(user: any, allReports: any[]): any[] {
  // If user has admin role, show all reports
  if (user?.roles?.includes('TIL.PMP.ADMIN')) {
    return allReports;
  }

  const userLead = getCurrentUserInitiativeLead(user);
  if (!userLead) {
    return allReports; // Default to all if we can't determine lead
  }

  // Filter reports to only those for projects/subawardees assigned to this lead
  return allReports.filter(report => {
    // Check if the report's component project is assigned to this lead
    const projectMatch = userLead.componentProjects.some(project => 
      project.toLowerCase() === report.componentProject.toLowerCase()
    );

    // Check if any of the report's subawardees are assigned to this lead
    const subawardeeMatch = report.subawardeeReports?.some((subReport: any) =>
      userLead.subawardees.includes(subReport.subawardee)
    );

    return projectMatch || subawardeeMatch;
  });
}

/**
 * Apply unified admin filters to component projects
 */
export function applyAdminFiltersToProjects(
  allProjects: any[],
  filters: {
    initiativeLead?: string | null;
    componentProject?: string | null;
    subawardee?: string | null;
  },
  allReports?: any[]
): any[] {
  let filtered = [...allProjects];

  // First, always filter to only show projects with outstanding items
  if (allReports && allReports.length > 0) {
    filtered = filtered.filter(project => {
      // Find reports for this component project
      const projectReports = allReports.filter(report => 
        report.componentProject.toLowerCase() === project.id.toLowerCase()
      );

      // Check if any of these reports have outstanding items
      const hasOutstandingItems = projectReports.some(report => {
        // Check for actionable subawardee reports
        const hasActionableSubawardees = (report.subawardeeReports || []).some((sub: any) => 
          sub.status === 'not_submitted' || sub.status === 'pending' || sub.status === 'in_review' || sub.status === 'needs_attention'
        );

        // Check for incomplete milestones
        const hasIncompleteMilestones = (report.milestones || []).some((milestone: any) =>
          milestone.status !== 'completed'
        );

        return hasActionableSubawardees || hasIncompleteMilestones;
      });

      return hasOutstandingItems;
    });
  }

  // Filter by initiative lead
  if (filters.initiativeLead) {
    const lead = INITIATIVE_LEADS.find(l => l.id === filters.initiativeLead);
    if (lead) {
      // If reports are provided, filter to projects that have outstanding items for this lead's subawardees
      if (allReports && allReports.length > 0) {
        const projectsWithOutstandingItems = filtered.filter(project => {
          // Find reports for this component project
          const projectReports = allReports.filter(report => 
            report.componentProject.toLowerCase() === project.id.toLowerCase()
          );

          // Check if any of these reports have outstanding items for this lead's subawardees
          const hasOutstandingItems = projectReports.some(report => {
            // Check for actionable subawardee reports
            const hasActionableSubawardees = (report.subawardeeReports || []).some((sub: any) => 
              lead.subawardees.includes(sub.subawardee) &&
              (sub.status === 'not_submitted' || sub.status === 'pending' || sub.status === 'in_review' || sub.status === 'needs_attention')
            );

            // Check for incomplete milestones for this lead's subawardees
            const hasIncompleteMilestones = (report.milestones || []).some((milestone: any) =>
              lead.subawardees.includes(milestone.subawardee) &&
              milestone.status !== 'completed'
            );

            return hasActionableSubawardees || hasIncompleteMilestones;
          });

          return hasOutstandingItems;
        });

        filtered = projectsWithOutstandingItems;
      } else {
        // If no reports data, fall back to showing projects assigned to this lead
        filtered = filtered.filter(project => 
          lead.componentProjects.includes(project.name)
        );
      }
    }
  }

  // Filter by component project
  if (filters.componentProject) {
    filtered = filtered.filter(project => 
      project.id === filters.componentProject
    );
  }

  // Filter by subawardee
  if (filters.subawardee) {
    const subawardee = SUBAWARDEE_INFO.find(s => s.id === filters.subawardee);
    if (subawardee && allReports && allReports.length > 0) {
      // Only show projects that have outstanding items for this specific subawardee
      const projectsWithOutstandingItems = filtered.filter(project => {
        // Find reports for this component project
        const projectReports = allReports.filter(report => 
          report.componentProject.toLowerCase() === project.id.toLowerCase()
        );

        // Check if any of these reports have outstanding items for this subawardee
        const hasOutstandingItems = projectReports.some(report => {
          // Check for actionable subawardee reports
          const hasActionableReports = (report.subawardeeReports || []).some((subReport: any) =>
            subReport.subawardee === filters.subawardee &&
            (subReport.status === 'not_submitted' || subReport.status === 'pending' || subReport.status === 'in_review' || subReport.status === 'needs_attention')
          );

          // Check for incomplete milestones for this subawardee
          const hasIncompleteMilestones = (report.milestones || []).some((milestone: any) =>
            milestone.subawardee === filters.subawardee &&
            milestone.status !== 'completed'
          );

          return hasActionableReports || hasIncompleteMilestones;
        });

        return hasOutstandingItems;
      });

      filtered = projectsWithOutstandingItems;
    } else if (subawardee) {
      // Fallback if no reports data
      filtered = filtered.filter(project => 
        project.name === subawardee.componentProject
      );
    }
  }

  return filtered;
}

/**
 * Filter quarters to only show actionable ones (not 100% approved)
 */
export function filterActionableReports(allReports: any[]): any[] {
  return allReports.filter(report => {
    // Show only quarters that need action:
    // 1. Not 100% approved
    // 2. Past due, pending review, upcoming, or incomplete
    const isActionable = report.overallStatus !== 'approved' || report.completionRate < 100;
    
    return isActionable;
  });
}

/**
 * Calculate auto-approval countdown for pending reports
 * Auto-approval occurs 14 days after the report due date
 */
export function calculateAutoApprovalDays(dueDate: string, submittedDate: string): number | undefined {
  const today = new Date();
  const reportDueDate = new Date(dueDate);
  const autoApprovalDate = new Date(reportDueDate.getTime() + (14 * 24 * 60 * 60 * 1000)); // 14 days after due date
  
  // Only calculate if the report is past due and before auto-approval date
  if (today > reportDueDate && today < autoApprovalDate) {
    return Math.ceil((autoApprovalDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
  }
  
  return undefined;
}

/**
 * Calculate if a report is overdue based on status and due date
 */
export function calculateOverdueStatus(report: any): any {
  const today = new Date();
  const dueDate = new Date(report.dueDate);
  const isPastDue = today > dueDate;
  
  if (!isPastDue) {
    return report; // Not past due, return as is
  }
  
  // Check if any subawardees have not_submitted or needs_attention status
  const hasOverdueItems = report.subawardeeReports.some((sub: any) => 
    sub.status === 'not_submitted' || sub.status === 'needs_attention'
  );
  
  if (hasOverdueItems) {
    const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 3600 * 24));
    return {
      ...report,
      overallStatus: daysOverdue > 60 ? 'critical' : 'overdue',
      daysOverdue: daysOverdue
    };
  }
  
  return report;
}

/**
 * Sort reports by priority (most urgent first)
 */
export function sortReportsByPriority(reports: any[]): any[] {
  const priorityOrder = {
    'critical': 1,
    'overdue': 2, 
    'urgent': 3,
    'incomplete': 4,
    'upcoming': 5,
    'approved': 6
  };

  return [...reports].sort((a, b) => {
    const aPriority = priorityOrder[a.overallStatus as keyof typeof priorityOrder] || 999;
    const bPriority = priorityOrder[b.overallStatus as keyof typeof priorityOrder] || 999;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same priority, sort by days overdue/until due
    if (a.daysOverdue && b.daysOverdue) {
      return b.daysOverdue - a.daysOverdue; // Most overdue first
    }
    if (a.daysUntilDue && b.daysUntilDue) {
      return a.daysUntilDue - b.daysUntilDue; // Soonest due first
    }

    return 0;
  });
}

/**
 * Apply unified admin filters to quarterly reports
 */
export function applyAdminFiltersToReports(
  allReports: any[],
  filters: {
    initiativeLead?: string | null;
    componentProject?: string | null;
    subawardee?: string | null;
  }
): any[] {
  // First calculate overdue status for all reports
  let reportsWithOverdueStatus = allReports.map(calculateOverdueStatus);
  
  // Then filter to actionable reports only
  let filtered = filterActionableReports(reportsWithOverdueStatus);

  // Then apply user-specific filters
  // Filter by initiative lead
  if (filters.initiativeLead) {
    const lead = INITIATIVE_LEADS.find(l => l.id === filters.initiativeLead);
    if (lead) {
      // Filter reports to only include those with outstanding items for this lead's subawardees
      filtered = filtered.filter(report => {
        // Check if any of this lead's subawardees have actionable reports
        const hasActionableSubawardees = (report.subawardeeReports || []).some((subReport: any) =>
          lead.subawardees.includes(subReport.subawardee) &&
          (subReport.status === 'not_submitted' || subReport.status === 'pending' || subReport.status === 'in_review' || subReport.status === 'needs_attention')
        );

        // Check if any of this lead's subawardees have incomplete milestones
        const hasIncompleteMilestones = (report.milestones || []).some((milestone: any) =>
          lead.subawardees.includes(milestone.subawardee) &&
          milestone.status !== 'completed'
        );

        return hasActionableSubawardees || hasIncompleteMilestones;
      }).map(report => {
        // Filter the report data to only show items relevant to this lead
        return {
          ...report,
          subawardeeReports: (report.subawardeeReports || []).filter((subReport: any) =>
            lead.subawardees.includes(subReport.subawardee)
          ),
          milestones: (report.milestones || []).filter((milestone: any) =>
            lead.subawardees.includes(milestone.subawardee)
          )
        };
      });
    }
  }

  // Filter by component project
  if (filters.componentProject) {
    const project = findComponentProjectById(filters.componentProject);
    if (project) {
      filtered = filtered.filter(report => 
        report.componentProject.toLowerCase() === project.name.toLowerCase()
      );
    }
  }

  // Filter by subawardee
  if (filters.subawardee) {
    // Filter reports to only include those with outstanding items for this specific subawardee
    filtered = filtered.filter(report => {
      // Check if this subawardee has actionable reports
      const hasActionableReports = (report.subawardeeReports || []).some((subReport: any) =>
        subReport.subawardee === filters.subawardee &&
        (subReport.status === 'not_submitted' || subReport.status === 'pending' || subReport.status === 'in_review' || subReport.status === 'needs_attention')
      );

      // Check if this subawardee has incomplete milestones
      const hasIncompleteMilestones = (report.milestones || []).some((milestone: any) =>
        milestone.subawardee === filters.subawardee &&
        milestone.status !== 'completed'
      );

      return hasActionableReports || hasIncompleteMilestones;
    }).map(report => {
      // Filter the report data to only show items relevant to this subawardee
      return {
        ...report,
        subawardeeReports: (report.subawardeeReports || []).filter((subReport: any) =>
          subReport.subawardee === filters.subawardee
        ),
        milestones: (report.milestones || []).filter((milestone: any) =>
          milestone.subawardee === filters.subawardee
        )
      };
    });
  }

  // Sort by priority (most urgent first)
  return sortReportsByPriority(filtered);
}

/**
 * Get available filter options based on user permissions
 */
export function getAvailableFilterOptions(user: any) {
  // If user has admin role, show all options
  if (user?.roles?.includes('TIL.PMP.ADMIN')) {
    return {
      leads: INITIATIVE_LEADS,
      projects: COMPONENT_PROJECTS,
      subawardees: SUBAWARDEE_INFO
    };
  }

  // Otherwise, filter based on user's initiative lead assignment
  const userLead = getCurrentUserInitiativeLead(user);
  if (!userLead) {
    return {
      leads: INITIATIVE_LEADS,
      projects: COMPONENT_PROJECTS,
      subawardees: SUBAWARDEE_INFO
    };
  }

  const availableProjects = COMPONENT_PROJECTS.filter(project => 
    userLead.componentProjects.includes(project.name)
  );

  const availableSubawardees = SUBAWARDEE_INFO.filter(sub => 
    userLead.subawardees.includes(sub.id)
  );

  return {
    leads: [userLead], // Only show their own lead
    projects: availableProjects,
    subawardees: availableSubawardees
  };
}

// Component Project Definitions
export const COMPONENT_PROJECTS = [
  {
    id: 'safe-t',
    name: 'SAFE-T',
    fullName: 'Secure Aviation Framework for Emerging Technologies',
    adminGroups: ['SAFE-T.ADMIN'],
    subawardees: ['Skyway Range', 'TU - SAFE-T', 'DronePort Network']
  },
  {
    id: 'aswp',
    name: 'ASWP', 
    fullName: 'Advanced Skills for the Workforce Program',
    adminGroups: ['ASWP.ADMIN'],
    subawardees: ['Asemio', 'inTulsa - ASWP', 'TU - ASWP', 'Atlas', 'THEC', 'TCC']
  },
  {
    id: 'cntrl',
    name: 'CNTRL',
    fullName: 'Control Tower Initiative',
    adminGroups: ['CNTRL.ADMIN'],
    subawardees: ['inTulsa - CNTRL']
  },
  {
    id: 'g-ace',
    name: 'G-ACE',
    fullName: 'Greenwood AI Community Engagement',
    adminGroups: ['G-ACE.ADMIN'],
    subawardees: ['Black Tech Street']
  },
  {
    id: 'arch',
    name: 'ARCH',
    fullName: 'Architecture Initiative',
    adminGroups: ['ARCH.ADMIN'],
    subawardees: ['TU - ARCH', 'OSU']
  }
];

export type ComponentProjectDefinition = (typeof COMPONENT_PROJECTS)[number];

/**
 * Match `component_project_id` from APIs to {@link COMPONENT_PROJECTS}.
 * Comparison is case-insensitive (e.g. API may send `CNTRL` while config id is `cntrl`).
 */
export function findComponentProjectById(
  componentProjectId: string | null | undefined
): ComponentProjectDefinition | undefined {
  if (!componentProjectId) return undefined;
  const needle = componentProjectId.trim().toLowerCase();
  return COMPONENT_PROJECTS.find((p) => p.id.toLowerCase() === needle);
}